import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { findCitizens } from "services/CitizenService";
import { CitizenContext } from "components/citizen/CitizenContext";
import { CitizenEditor } from "components/citizen/CitizenEditor";
import { X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import BlockDetail from "components/block-detail/BlockDetail";
import { checkDateMonth, dateFormat1, dateFormat2, formatDate, range2 } from "utils/date";
import { Calendar } from "primereact/calendar";

export const CitizenList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["citizen", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const citizenContext = useContext(CitizenContext);
    const { citizen, setCitizen, citizens, setCitizens, setCitizenDialog } = citizenContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(false);

    const now = new Date();

    const beforeNow = new Date(new Date().setDate(new Date().getDate() - range2));

    const [formatDateVal, setFormatDateVal] = useState(dateFormat1);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            idNumber: { value: "", matchMode: "contains" },
            phoneNumber: { value: "", matchMode: "contains" },
            fullName: { value: "", matchMode: "contains" },
            birthdate: { value: null, matchMode: "equals" },
            gender: { value: null, matchMode: "equals" },
            state: { value: null, matchMode: "equals" },
            fromDate: { value: beforeNow, matchMode: "equals" },
            toDate: { value: now, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const genders = [
        { label: t("male", { ns: "common" }), value: "1" },
        { label: t("female", { ns: "common" }), value: "0" },
    ];

    const states = [
        { label: "Chưa xác thực với DC", value: "UNVERIFIED" },
        { label: "Đã xác thực với DC", value: "VERIFIED" },
        { label: "Xác thực lỗi với DC", value: "FAILED" },
    ];

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);

        findCitizens(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setCitizens(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openDialog = (citizen) => {
        setCitizen({ ...citizen });
        setCitizenDialog(true);
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Số CCCD */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "66px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="idNumber"
                    >
                        {t("citizen.idNumber")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="idNumber"
                            name="idNumber"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        idNumber: {
                                            ...lazyParams.filters.idNumber,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* Số điện thoại  */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="phoneNumber"
                    >
                        {t("citizen.phoneNumber")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            value={lazyParams?.filters?.phoneNumber.value}
                            id="phoneNumber"
                            name="phoneNumber"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        phoneNumber: {
                                            ...lazyParams.filters.phoneNumber,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* Ngày nhận từ */}
            <div className="col-12 md:col-2">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "100px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="fromDate"
                    >
                        {t("citizen.fromDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.fromDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        fromDate: {
                                            ...lazyParams.filters.fromDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="fromDate"
                            id="fromDate"
                            maxDate={lazyParams.filters.toDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                        {/* <InputText
                            style={{
                                width: "100%",
                                // margin: "5px 0px 0px 0px"
                            }}
                            type="date"
                            id="fromDate"
                            name="fromDate"
                            value={lazyParams.filters.fromDate.value}
                            max={lazyParams.filters.toDate.value}
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        fromDate: {
                                            ...lazyParams.filters.fromDate,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText> */}
                    </div>
                </div>
            </div>

            {/* đến */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="toDate"
                    >
                        {t("citizen.toDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.toDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        toDate: {
                                            ...lazyParams.filters.toDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="toDate"
                            id="toDate"
                            // maxDate={now}
                            minDate={lazyParams.filters.fromDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                        {/* <InputText
                            style={{
                                width: "100%",
                                // margin: "5px 0px 0px 0px",
                            }}
                            type="date"
                            id="toDate"
                            name="toDate"
                            value={lazyParams.filters.toDate.value}
                            max={now}
                            min={lazyParams.filters.fromDate.value}
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        toDate: {
                                            ...lazyParams.filters.toDate,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText> */}
                    </div>
                </div>
            </div>

            <div className="col-12 md:col-1 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                if (lazyParams.filters.idNumber.value || lazyParams.filters.phoneNumber.value) {
                                    setCheckLoad(true);
                                    setLazyParams({
                                        ...lazyParams,
                                        first: 0,
                                        page: 0,
                                    });
                                } else {
                                    if (lazyParams.filters.fromDate.value && lazyParams.filters.toDate.value) {
                                        if (checkDateMonth(lazyParams.filters.fromDate.value, lazyParams.filters.toDate.value, range2)) {
                                            setCheckLoad(true);
                                            setLazyParams({
                                                ...lazyParams,
                                                first: 0,
                                                page: 0,
                                            });
                                        } else {
                                            toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("citizen.rangeFromToDate"), life: 3000 });
                                        }
                                    } else {
                                        toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("citizen.requiredfromandtodate"), life: 3000 });
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterCallback(e.target?.value || "")} />
            </div>
        );
    };

    const genderFilterTemplate = (options) => {
        return <Dropdown style={{ width: "100%", margin: "auto", height: "auto", padding: "10px" }} value={options.value} options={genders} onChange={(e) => options.filterCallback(e.value)} showClear={true} />;
    };

    const stateFilterTemplate = (options) => {
        return <Dropdown style={{ width: "100%", margin: "auto", height: "auto", padding: "10px" }} value={options.value} options={states} onChange={(e) => options.filterCallback(e.value)} showClear={true} />;
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("citizen.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const genderBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{genders.find((g) => g.value === `${rowData.gender}`)?.label}</p>
                </div>
            </>
        );
    };

    const stateBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{states.find((g) => g.value === rowData.state)?.label}</p>
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openDialog(rowData)} />
            </div>
        );
    };

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("citizen.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("citizen.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={citizens}
                                    dataKey="idNumber"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="idNumber" sortable header={t("idNumber")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="phoneNumber" sortable header={t("phoneNumber")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="fullName" sortable header={t("fullName")} showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="birthdate" header={t("birthdate")} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="gender" header={t("gender")} showFilterMatchModes={false} filterElement={genderFilterTemplate} body={genderBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="state" header={t("state")} showFilterMatchModes={false} filterElement={stateFilterTemplate} body={stateBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column body={actionBodyTemplate} style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>
                    <CitizenEditor />
                </div>
            </div>
        </div>
    );
};
